<template>
	<component :is="layout">
		<router-view />
	</component>
	<Toaster />
	<AlertWidget v-if="user && isGraphView && isDeveloper" />
</template>

<script lang="ts" setup>
	import { ref, computed, watch, onMounted, getCurrentInstance } from 'vue'
	// import { useStore } from 'vuex'
	import { useRoute } from 'vue-router'
	// import { ModalsContainer } from 'vue-final-modal'
	import { useBaseStore } from '@/stores/base.store'
	import AuthLayout from '@/layouts/AuthLayout.vue'
	import { useAuthStore } from './stores/auth.store'
	import type { IUser } from './stores/auth.store'
	import { useComappStore } from './stores/comapps.store'
	import { useModuleStore } from './stores/modules.store'
	import { startVersionChecker } from '@/libs/versionChecker'
	import Toaster from '@/components/common/Toaster.vue'
	import AlertWidget from '@/features/alerts/AlertWidget.vue'
	import { closeAllModals } from '@/libs/modals'
	// import MainLayout from '@/layouts/MainLayout.vue'

	// --- Composition API setup ---
	// const store = useStore()
	const authStore = useAuthStore()
	const baseStore = useBaseStore()
	const comappStore = useComappStore()
	const modulStore = useModuleStore()
	const route = useRoute()
	// const router = useRouter()
	const { proxy } = getCurrentInstance() as any
	const socket = proxy.$socket

	// --- Constants ---
	const layout = computed(() => {
		// console.log(route.meta);
		// return route.meta.layoutComponent || MainLayout
		return route.meta.layoutComponent || AuthLayout
	})

	// --- State & refs ---
	const closedMenu = ref(true)
	const closedSidePanel = ref(true)
	const showAlertWidgetModal = ref(false)
	// const sc = ref(store.state.sc)

	// --- Computed (mapped state & getters) ---
	// const userTheme = computed(() => store.state.userTheme)
	// const showLoginModal = computed(() => store.state.showLoginModal)
	// const wsCount = computed(() => store.state.wsCount)
	// const wsConnected = computed(() => store.state.wsConnected)
	// const version = computed(() => store.state.version)
	const user = computed(() => authStore.user)
	// Alarm-Sidebar nur auf Graph-Seiten: getActiveIds.graphId ist genau dann
	// gesetzt, wenn die aktuelle /modules/:id-Route einen Custom-Graph meint.
	// Die Glocke ist Entwickler-only — normale Nutzer erreichen die Alarm-Liste
	// über den „Alarme"-Reiter im Alert-Flags-Modal.
	const isGraphView = computed(() => !!baseStore.getActiveIds.graphId)
	const isDeveloper = computed(() => ['developer'].includes(authStore.user?.role || ''))
	// const role = computed(() => authStore.role)
	// const sensorAlertsDates = computed(() => store.getters.sensorAlertsDates)
	// const locationsObj = computed(() => store.getters.locationsObj)
	// const projectsObj = computed(() => store.getters.projectsObj)
	// const hubsObj = computed(() => store.getters.hubsObj)
	// const commappByPcName = computed(() => store.getters.commappByPcName)

	// const routeName = computed(() => route.name?.toString() || '')
	// const routes = computed(() => {
	// 	const groups: Record<string, any[]> = {}
	// 	store.$router?.options.routes.forEach((el: any) => {
	// 		if (el.meta?.group) {
	// 			const allowed: string[] = el.meta.allowedRoles || []
	// 			if (allowed.includes(role.value)) {
	// 				const item = { name: el.name, path: el.path, icon: el.meta.icon || null }
	// 				if (!groups[el.meta.group]) groups[el.meta.group] = []
	// 				groups[el.meta.group].push(item)
	// 			}
	// 		}
	// 	})
	// 	return groups
	// })
	// const sidePanelClass = computed(() => ({ _closed: closedSidePanel.value }))

	// --- Actions ---
	// const _set = (payload: any) => store.dispatch('_set', payload)
	// const _initAdmin = () => store.dispatch('_initAdmin')

	// --- Watchers ---
	// watch(
	// 	() => i18n.locale.value,
	// 	(val) => {
	// 		_set(['locale', val])
	// 	},
	// 	{ immediate: true },
	// )

	// watch(
	// 	() => socket.connected,
	// 	(val) => {
	// 		console.log('$socket.connected', val)
	// 		_set(['wsConnected', val])
	// 	},
	// 	{ immediate: true },
	// )

	watch(route, (to, from) => {
		if (from.hash.includes('modal')) {
			closeAllModals()
			window.location.hash = ''
		}
		closedMenu.value = true
		closedSidePanel.value = true
		showAlertWidgetModal.value = false
	})
	watch(
		() => user.value,
		(nv: IUser | null = null, ov: IUser | null = null) => {
			const email = nv?.email || ''
			const changed = email !== (ov?.email || '')
			if (email.length < 4 && socket.connected) {
				disconnectToWs()
			} else if (changed && email.length >= 4 && !socket.connected) {
				// _initAdmin()
				if (authStore.isAdmin) {
					// setTimeout(() => baseStore.initAdmin(), 1000)
					baseStore.initAdmin()
					comappStore.listComapps()
					modulStore.listAllModules()
				} else {
					baseStore.initBasicUser()
					modulStore.listAllModules()
				}
			}
		},
		{ immediate: true },
	)

	// watch(
	// 	() => route.params,
	// 	(nv: any) => {
	// 		_set(['locationIdRoute', nv.locationId || ''])
	// 		let proj = nv.projectId || ''
	// 		let loc = nv.locationId || ''
	// 		_set(['projectIdRoute', nv.projectId || ''])
	// 		_set(['modulIdRoute', nv.modulId || ''])
	// 		_set(['hubIdRoute', nv.hubId || ''])
	// 		_set(['comappIdRoute', nv.comappId || ''])
	// 		// if (nv.hubLocProId) {
	// 		// 	_set(['hubIdRoute', nv.hubLocProId])
	// 		// 	loc = locationsObj.value[nv.hubLocProId]
	// 		// 		? nv.hubLocProId
	// 		// 		: hubsObj.value[nv.hubLocProId]?.locationId || ''
	// 		// 	_set(['locationIdRoute', loc])
	// 		// 	proj = projectsObj.value[nv.hubLocProId] ? nv.hubLocProId : locationsObj.value[loc]?.projectId || ''
	// 		// } else if (nv.comappId) {
	// 		// 	loc = commappByPcName.value[nv.comappId]?.locationId || ''
	// 		// 	_set(['locationIdRoute', loc])
	// 		// 	proj = locationsObj.value[loc]?.projectId || ''
	// 		// }
	// 		if (nv.locationId) proj = locationsObj.value[nv.locationId]?.projectId || ''
	// 		_set(['projectIdCalculated', proj || ''])
	// 	},
	// 	{ immediate: true, deep: true },
	// )

	// --- Lifecycle ---
	onMounted(async () => {
		startVersionChecker()
		if (user.value?.email) {
			// console.log(`🟢 onMounted: user=${JSON.stringify(user.value)}`)
			await authStore.refreshAccessToken()
			setInterval(() => baseStore.initAdmin(), 1000 * 60 * 30)
		}
	})

	// --- Methods ---
	// const connectToWs = debounce(() => {
	// 	if (proxy.isDev) {
	// 		proxy.$socket.client.disconnect()
	// 		if (!socket.connected) {
	// 			proxy.$socket.client.io.opts.extraHeaders = { accesstoken: user.value.accessToken }
	// 			proxy.$socket.client.connect()
	// 		}
	// 	}
	// }, 900)

	function disconnectToWs() {
		/* if (socket.connected) proxy.$socket.client.disconnect() */
	}

	// function triggerMenu(state = '') {
	// 	if (!state) closedMenu.value = !closedMenu.value
	// 	else closedMenu.value = state !== 'open'
	// }

	// function triggerSidePanel(state = '') {
	// 	if (!state) closedSidePanel.value = !closedSidePanel.value
	// 	else closedSidePanel.value = state !== 'open'
	// }
</script>

<style scoped>
	@reference '@/styles/tailwind.css';

	/* @import '@/assets/styles/colors.scss'; */
	.wrapper {
		display: flex;
		min-height: 100%;
		height: calc(100vh - var(--header-height-mobile));
	}

	@media only screen and (min-width: 581px) {
		.wrapper {
			height: calc(100vh - var(--header-height));
		}
	}

	.admin-layout {
		@apply grid h-screen;
	}

	@media screen and (min-width: 641px) {
		.admin-layout {
			grid-template-columns: 16rem 1fr;
			grid-template-rows: 4rem 1fr auto;
			grid-template-areas:
				'headspace headspace'
				'content content'
				'footer footer';
		}
	}

	@media screen and (max-width: 640px) {
		.admin-layout {
			grid-template-columns: 0rem 1fr;
			grid-template-rows: 4rem 1fr auto;
			grid-template-areas:
				'headspace headspace'
				'content content'
				'footer footer';
		}
	}

	.headspace {
		grid-area: headspace;
		@apply z-20;
	}

	.content {
		grid-area: content;
		overflow-y: auto;
		@apply h-full;
		@apply bg-base-100;
	}

	.footer {
		grid-area: footer;
		@apply h-6;
		@apply font-light;
		@apply bg-base-300;
	}

	/*
nav {
	@apply transition-all w-0 overflow-hidden;
	height: calc(100vh - var(--header-height));
}

#nav {
	height: calc(100vh - var(--header-height));
	width: var(--nav-width);
	@apply absolute overflow-auto bg-base-100 border-r border-base-content/25;
}

.backheader {
	transition: width 0s, opacity 0.25s, translateX 0s;
	background: #333a;
	width: calc(100vw + 200px);
	height: calc(100vh - var(--header-height));
	overflow: hidden;
	position: fixed;
	top: 0;
	left: 0;
	z-index: 1;
	opacity: 1;
	transform: translateX(0px);
}

@media only screen and (max-width: var(--mobile-trigger)) {
	nav {
		z-index: 20;
	}

	#nav {
		position: relative;
		z-index: 20;
	}

	nav._hide {
		width: 0;
	}

	nav._hide .backheader,
	nav._hide #nav {
		width: 0;
	}

	nav._access {
		overflow: initial;
		position: absolute;
		width: var(--nav-width);
		min-width: var(--nav-width);
		height: 100%;
		transform: translateX(0px);
	}

	nav._closed {
		transform: translateX(-var(--nav-width));
	}

	nav._closed .backheader {
		opacity: 0;
		width: 0px;
	}

	.topMobileSpace {
		margin: 3rem;
	}
}

@media only screen and (min-width: var(--mobile-triggerP)) {
	#nav {
		z-index: 1;
	}

	nav._hide {
		width: 0;
	}

	nav._hide .backheader,
	nav._hide #nav {
		width: 0;
	}

	nav._access {
		position: relative;
		overflow: initial;
		width: var(--nav-width);
		min-width: var(--nav-width);
	}

	nav._access .backheader {
		display: none;
		width: 0;
	}
} */

	/*
header {
	position: relative;
	height: var(--header-height);
	width: 100%;
	display: flex;
}

@media only screen and (max-width: var(--mobile-triggerP)) {
	header {
		height: var(--header-height-mobile);
	}
}

.partLeft {
	@apply h-full overflow-hidden;
	width: var(--nav-width);
	line-height: var(--header-height);
}

.partRight {
	line-height: var(--header-height);
	height: 100%;
	width: calc(100% - var(--nav-width));
}

.partRight a {
	cursor: pointer;
}

.menuBtn {
	position: fixed;
	left: 0;
	top: 0;
	z-index: 322;
}

@media only screen and (min-width: var(--mobile-triggerP)) {
	.menuBtn {
		display: none;
	}
}

main {
	flex: 1;
	height: 100%;
	overflow: hidden;
	display: flex;
}

.sidePanel1,
.sidePanel2,
.sidePanel3 {
	z-index: 1;
	width: var(--panel-width);
}

@media only screen and (max-width: var(--mobile-trigger)) {

	.sidePanel1,
	.sidePanel2,
	.sidePanel3 {
		transition: all 0.25s;
		position: absolute;
		top: 0;
		left: 0;
		transform: translateX(0px);
		box-shadow: 0 0 24px #00000070;
	}

	.sidePanel1._closed,
	.sidePanel2._closed,
	.sidePanel3._closed {
		transform: translateX(-var(--panel-width));
		box-shadow: 0 0 0 #0000;
	}
}

.mainPanel {
	overflow: auto;
	width: calc(100% - var(--panel-width));
}

.fade-enter-active,
.fade-leave-active {
	transition: opacity 0.5s;
}

.fade-enter,
.fade-leave-to {
	opacity: 0;
}

.version {
	@apply fixed bottom-0 left-0 p-2 shadow-2xl text-sm text-secondary backdrop-blur-md flex items-center gap-3 justify-between;
	width: calc(var(--nav-width) - 0rem);
}

.themeswitch {
	position: fixed;
	bottom: 4px;
	left: calc(var(--nav-width) - 26px);
}

.themeswitch i {
	font-size: 1.5rem;
}

@media only screen and (max-width: var(--mobile-trigger)) {
	.mobileSpace {
		margin: 2rem;
	}
} */
</style>
